import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Target, Eye, ArrowRight, Compass, Sparkles, HeartHandshake, Wrench } from "lucide-react";

const stats = [
  { value: "10+", label: "Years combined experience" },
  { value: "40+", label: "Certified engineers" },
  { value: "99.99%", label: "Aggregate uptime" },
  { value: "3", label: "Cloud platforms mastered" },
];

const values = [
  { icon: Compass, title: "Pragmatic over pure", desc: "We optimize for what ships and stays up — not for architectural purity." },
  { icon: HeartHandshake, title: "Extension of your team", desc: "Named engineers, shared Slack, pairing sessions. We're on your side." },
  { icon: Sparkles, title: "Rigor with taste", desc: "Every runbook, diagram, and dashboard is crafted like a product." },
  { icon: Wrench, title: "Handoff by default", desc: "Everything lives in your accounts, in your repos, documented for your team." },
];

const About = () => {
  return (
    <Layout>
      <Helmet>
        <title>About — Cloudease</title>
        <meta name="description" content="Cloudease is a team of certified cloud and DevOps engineers building production infrastructure for modern companies." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-aurora animate-aurora" aria-hidden />
        <div className="container relative mx-auto px-4 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-widest text-primary mb-5">About</p>
            <h1 className="text-5xl md:text-7xl font-heading font-semibold tracking-tight mb-6">
              We build the <span className="text-gradient">boring infrastructure</span> so your product feels magic.
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Cloudease is a Bengaluru-headquartered team of cloud, DevOps, and SRE engineers. We help ambitious companies operate infrastructure the way Stripe, Linear, and Vercel do — without hiring for it.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="py-16 border-b border-border/60">
        <div className="container mx-auto px-4 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="text-4xl md:text-5xl font-heading font-semibold text-foreground">{s.value}</div>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-24 border-b border-border/60">
        <div className="container mx-auto px-4 lg:px-8 grid md:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-10 rounded-2xl glass">
            <Target className="h-6 w-6 text-primary mb-6" />
            <h2 className="text-2xl font-heading font-semibold mb-3">Mission</h2>
            <p className="text-muted-foreground leading-relaxed">
              Make enterprise-grade cloud infrastructure accessible to every ambitious team. Cloud complexity shouldn't be a moat — it should be a solved problem.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="p-10 rounded-2xl glass">
            <Eye className="h-6 w-6 text-primary mb-6" />
            <h2 className="text-2xl font-heading font-semibold mb-3">Vision</h2>
            <p className="text-muted-foreground leading-relaxed">
              To be the most trusted cloud partner in Asia by 2030 — enabling 1,000+ companies to run world-class infrastructure without a full platform org.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 border-b border-border/60">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-mono uppercase tracking-widest text-primary mb-4">How we operate</p>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight">Principles, not slogans.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="p-6 rounded-2xl glass hover:border-primary/40 transition-colors"
              >
                <v.icon className="h-5 w-5 text-primary mb-4" />
                <h3 className="font-heading font-semibold text-lg mb-2">{v.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh opacity-40 animate-aurora" aria-hidden />
        <div className="container relative mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight mb-5">
            Let's build something <span className="text-gradient">that stays up.</span>
          </h2>
          <p className="text-muted-foreground mb-8">A 30-minute call. No pitch decks.</p>
          <Link to="/contact">
            <Button variant="gradient" size="xl" className="rounded-full">
              Book a call <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default About;
